import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Cog, X, ZoomIn, ChevronLeft as ChevronLeftIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from './Navbar';
import Footer from './Footer';

// Lightbox Component
const Lightbox = ({ image, onClose }) => {
  if (!image) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95"
        onClick={onClose}
      >
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="absolute top-4 right-4 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors"
          onClick={onClose}
        >
          <X size={24} className="text-white" />
        </motion.button>
        
        <motion.img
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.8, opacity: 0 }}
          src={image}
          alt="Enlarged view"
          className="max-w-[90vw] max-h-[90vh] object-contain"
          onClick={(e) => e.stopPropagation()}
        />
      </motion.div>
    </AnimatePresence>
  );
};

// Image Carousel Component
const ImageCarousel = ({ images, onImageClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) {
    return (
      <div className="card-technical p-12 text-center h-80 flex items-center justify-center">
        <p className="text-[#94A3B8] text-lg">Images coming soon...</p>
      </div>
    );
  }

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <div className="relative">
      {/* Main Image Display */}
      <div className="relative overflow-hidden border border-[#6B8CAE]/30 bg-white/5 h-80">
        <motion.img
          key={currentIndex}
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -50 }}
          transition={{ duration: 0.3 }}
          src={images[currentIndex]}
          alt={`Image ${currentIndex + 1}`}
          className="w-full h-full object-contain cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        />
        
        {/* Zoom Icon Overlay */}
        <div 
          className="absolute inset-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity bg-black/20 cursor-zoom-in"
          onClick={() => onImageClick(images[currentIndex])}
        >
          <div className="w-12 h-12 bg-[#6B8CAE] flex items-center justify-center">
            <ZoomIn size={24} className="text-white" />
          </div>
        </div>

        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronLeftIcon size={20} className="text-white" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0F172A]/80 border border-[#6B8CAE]/50 flex items-center justify-center hover:bg-[#6B8CAE]/20 transition-colors"
            >
              <ChevronRight size={20} className="text-white" />
            </button>
          </>
        )}

        {/* Image Counter */}
        <div className="absolute bottom-3 right-3 bg-[#0F172A]/80 px-3 py-1 text-white text-sm font-mono">
          {currentIndex + 1} / {images.length}
        </div>
      </div>

      {/* Thumbnail Strip */}
      {images.length > 1 && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`flex-shrink-0 w-20 h-16 border-2 overflow-hidden transition-all ${
                index === currentIndex 
                  ? 'border-[#6B8CAE] opacity-100' 
                  : 'border-transparent opacity-50 hover:opacity-75'
              }`}
            >
              <img
                src={img}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const IndustrialAutomationPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [lightboxImage, setLightboxImage] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const texts = {
    en: {
      back: 'Back to Services',
      title: 'Industrial Automation',
      description: 'Industrial automation helps manufacturers improve consistency, increase throughput and reduce dependence on unstable manual processes. It is especially valuable for companies that need reliable, scalable and production-ready solutions for repetitive operations, machine loading, material handling and process control. We develop and integrate automation systems with a practical engineering approach, focused on reliability, maintainability and long-term operational value.',
      clickToZoom: 'Click image to enlarge',
      items: [
        'Custom automation machinery and equipment',
        'Industrial robot integration',
        'Machine tending solutions',
        'Palletizing systems',
        'Vision system integration'
      ]
    },
    bg: {
      back: 'Обратно към Услуги',
      title: 'Индустриална автоматизация',
      description: 'Индустриалната автоматизация помага на производителите да подобрят последователността, да увеличат производителността и да намалят зависимостта от нестабилни ръчни процеси. Тя е особено ценна за компании, които се нуждаят от надеждни, мащабируеми и готови за производство решения за повтарящи се операции, зареждане на машини, обработка на материали и контрол на процеси. Ние разработваме и интегрираме системи за автоматизация с практичен инженерен подход, фокусиран върху надеждност, поддържаемост и дългосрочна оперативна стойност.',
      clickToZoom: 'Кликнете върху изображението за увеличаване',
      items: [
        'Персонализирани машини и оборудване за автоматизация',
        'Интеграция на индустриални роботи',
        'Решения за обслужване на машини',
        'Палетизиращи системи',
        'Интеграция на визуални системи'
      ]
    }
  };

  const t = texts[language] || texts.en;

  // Images for Industrial Automation (Pics: 6, 15, 16, 17, 19, 20)
  // Currently have: 6, 15, 16, 17
  const images = [
    'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/k6q9ycdp_Picture6.jpg',
    'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/pzp7u0nf_Picture15.jpg',
    'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/ro88mnhd_Picture16.jpg',
    'https://customer-assets.emergentagent.com/job_esot-engineering/artifacts/7p08k7y4_Picture17.jpg'
  ];

  return (
    <div className="min-h-screen bg-[#0F172A]">
      <Navbar />
      
      {/* Lightbox */}
      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}
      
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-[#6B8CAE] hover:text-white transition-colors mb-8"
            data-testid="back-button"
          >
            <ArrowLeft size={20} />
            <span className="font-mono text-sm uppercase tracking-wider">{t.back}</span>
          </motion.button>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-12"
          >
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-[#6B8CAE]/10 border border-[#6B8CAE]/30 flex items-center justify-center">
                <Cog size={32} className="text-[#6B8CAE]" />
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-white" data-testid="automation-page-title">
                {t.title}
              </h1>
            </div>
            <p className="text-[#94A3B8] text-lg leading-relaxed max-w-4xl">
              {t.description}
            </p>
          </motion.div>

          {/* Content Grid */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid lg:grid-cols-2 gap-12"
          >
            {/* Items List */}
            <div className="card-technical p-8">
              <h2 className="text-2xl font-semibold text-white mb-8 flex items-center space-x-3">
                <Cog size={24} className="text-[#6B8CAE]" />
                <span>{language === 'bg' ? 'Нашите услуги' : 'Our Services'}</span>
              </h2>
              <ul className="space-y-4">
                {t.items.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start space-x-3 text-[#94A3B8]"
                  >
                    <ChevronRight size={20} className="text-[#6B8CAE] flex-shrink-0 mt-0.5" />
                    <span className="text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Images Carousel */}
            <div>
              <ImageCarousel 
                images={images} 
                onImageClick={(img) => setLightboxImage(img)}
              />
              {images.length > 0 && (
                <p className="text-[#6B8CAE] text-sm mt-3 text-center font-mono">
                  <ZoomIn size={14} className="inline mr-1" />
                  {t.clickToZoom}
                </p>
              )}
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default IndustrialAutomationPage;
